/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { CustomDialog, ToolContainer } from '@opengeoweb/shared';
import { BackdropStyle } from '@opengeoweb/theme';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { WorkspaceSelectListConnect } from '../WorkspaceSelectList/WorkspaceSelectListConnect';

export interface WorkspaceMenuProps {
  closeMenu?: () => void;
  isOpen?: boolean;
  initialIsLabelFilterOpen?: boolean;
}

export const WorkspaceMenu: React.FC<WorkspaceMenuProps> = ({
  isOpen = false,
  closeMenu,
  initialIsLabelFilterOpen = false,
}: WorkspaceMenuProps) => {
  const { t } = useWorkspaceTranslation();
  const [isLabelFilterOpen, setIsLabelFilterOpen] = React.useState(
    initialIsLabelFilterOpen,
  );

  const handleToggleLabelFilter = React.useCallback(() => {
    setIsLabelFilterOpen((prev) => !prev);
  }, []);

  const dialogWidth = isLabelFilterOpen ? 700 : 483;
  return (
    <CustomDialog
      data-testid="workspace-menu"
      hasLayout={false}
      isResizable
      key={isLabelFilterOpen ? 'wide' : 'narrow'}
      minWidth={isLabelFilterOpen ? 700 : undefined}
      onClose={(): void => {
        if (closeMenu) {
          closeMenu();
        }
      }}
      open={isOpen}
      resizableDefaultSize={{ width: dialogWidth, height: '90%' }}
      slotProps={{
        backdrop: {
          style: {
            top: 40,
            ...BackdropStyle(),
          },
        },
        root: {
          style: {
            top: 40,
          },
        },
      }}
    >
      <ToolContainer
        onClick={(event): void => {
          event.stopPropagation();
        }}
        onClose={closeMenu}
        style={{ width: '100%', height: '100%' }}
        title={t('workspace-menu')}
      >
        <WorkspaceSelectListConnect
          isLabelFilterOpen={isLabelFilterOpen}
          onToggleLabelFilter={handleToggleLabelFilter}
        />
      </ToolContainer>
    </CustomDialog>
  );
};
